import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Seo, faqSchema } from "@/components/Seo";
import { Reveal, MaskLines } from "@/components/Reveal";
import { Img, SectionHead, Marquee, ServiceCard, IndustryCard, FaqList, Steps } from "@/components/Blocks";
import { StatsBar } from "@/components/StatsBar";
import { ClientLogos } from "@/components/ClientLogos";
import { Testimonials } from "@/components/Testimonials";
import { Icon, WhatsAppIcon } from "@/components/Icon";
import { InquiryForm } from "@/components/InquiryForm";
import { SERVICES } from "@/data/services";
import { INDUSTRY_PAGES, ALL_INDUSTRIES } from "@/data/industries";
import { CHALLENGES, SOLUTION_GROUPS, SYSTEMS, WHY, PROCESS, BUSINESS_TYPES, HOME_FAQS } from "@/data/home";
import { WA_DEFAULT, TAGLINE } from "@/data/site";

const ease = [0.22, 1, 0.36, 1];

const Badge = () => (
  <div className="pointer-events-none absolute -left-10 -top-10 z-10 hidden h-36 w-36 lg:block" aria-hidden="true">
    <svg viewBox="0 0 200 200" className="h-full w-full animate-spin-slow">
      <defs><path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
      <circle cx="100" cy="100" r="99" fill="#F8F6F1" />
      <text className="font-mono" fontSize="13" letterSpacing="4.2" fill="#272923">
        <textPath href="#circ">POWERING THE FUTURE · OF MANUFACTURING · </textPath>
      </text>
    </svg>
    <img src="/brand/mark.png" alt="" className="absolute left-1/2 top-1/2 h-12 w-auto -translate-x-1/2 -translate-y-1/2" />
  </div>
);

const HeroVisual = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <Badge />
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ duration: 1.4, ease, delay: 0.35 }}
        className="relative aspect-[4/5] overflow-hidden rounded-md bg-charcoal sm:aspect-[16/11] lg:aspect-[4/5]"
      >
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <Img name="hero" alt="Robotic production line inside a modern manufacturing facility" eager sizes="(min-width: 1024px) 45vw, 100vw" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 text-ivory sm:p-7">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#EADFD2]">Connected operations<br />Plant · Office · Market</p>
          <span className="font-mono text-[11px] text-[#EADFD2]">IX—01</span>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease, delay: 1.1 }}
        style={{ translateZ: 60 }}
        className="relative mt-4 w-full rounded-md border border-stone bg-white/95 p-5 shadow-[0_30px_60px_-30px_rgba(39,41,35,.45)] backdrop-blur sm:absolute sm:-bottom-10 sm:-left-8 sm:mt-0 sm:w-[300px]"
        data-testid="hero-system-card"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-rust-ink">One connected system</p>
        <ul className="mt-3 space-y-2 text-[13px] text-charcoal">
          {["Orders & quotations", "Production & inventory", "Dealers & suppliers", "Website & SEO"].map((t) => (
            <li key={t} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-sage" />{t}</li>
          ))}
        </ul>
      </motion.div>
    </div>
  );
};

const Hero = () => (
  <section className="relative overflow-hidden" data-testid="home-hero">
    <div className="grid-lines absolute inset-0 [mask-image:linear-gradient(to_bottom,black,transparent)]" aria-hidden="true" />
    <div className="wrap relative grid gap-14 pb-24 pt-10 lg:grid-cols-[1.12fr_0.88fr] lg:items-center lg:gap-16 lg:pb-32 lg:pt-16">
      <div>
        <motion.p className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>Digital transformation for industry</motion.p>
        <MaskLines
          lines={["Smart Digital", "Solutions for", <span key="m">Manufacturing <span className="text-rust">&amp;</span></span>, "Industrial Businesses"]}
          className="mt-7 text-[2.4rem] font-extrabold leading-[0.98] sm:text-6xl lg:text-[3.55rem] xl:text-[3.95rem]"
          testId="hero-title"
        />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease, delay: 0.8 }}>
          <p className="lead mt-8 max-w-xl">
            Industrix Solution helps manufacturers, factories, industrial suppliers, and engineering companies modernize their operations through custom software, business websites, ERP and CRM systems, automation, mobile applications, and digital growth solutions.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/contact#inquiry" className="btn-primary group" data-testid="hero-discuss-btn">Discuss Your Project <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
            <Link to="/services" className="btn-ghost" data-testid="hero-services-btn">Explore Our Services</Link>
          </div>
          <p className="mt-10 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-graphite" data-testid="hero-trust">
            <span className="h-2 w-2 rounded-full bg-sage" aria-hidden="true" /> Built for industrial businesses. Designed for global growth.
          </p>
        </motion.div>
      </div>
      <HeroVisual />
    </div>
  </section>
);

const Challenges = () => (
  <section className="wrap py-24 lg:py-36" data-testid="challenges-section">
    <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
      <div className="lg:sticky lg:top-28 lg:self-start">
        <SectionHead eyebrow="The problem" title="Technology That Solves Real Industrial Challenges" lead="Most industrial businesses do not lack effort or expertise. They lack connected information. The right digital tools give owners and managers visibility, help teams coordinate, remove repetitive work and support faster, better-informed decisions." />
      </div>
      <ol className="grid gap-px overflow-hidden rounded-md border border-stone bg-stone sm:grid-cols-2">
        {CHALLENGES.map(([t, d], i) => (
          <Reveal as="li" key={t} delay={(i % 2) * 0.08} className="group bg-ivory p-7 transition-colors duration-500 hover:bg-white">
            <span className="font-mono text-xs text-rust-ink">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-6 font-display text-lg font-bold leading-snug">{t}</h3>
            <p className="mt-2 text-[14px] leading-relaxed text-graphite">{d}</p>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const Services = () => (
  <section className="border-t border-stone bg-[#F1EEE7] py-24 lg:py-36" data-testid="services-section">
    <div className="wrap">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <SectionHead eyebrow="What we build" title="Complete Digital Solutions for Industrial Businesses" />
        <Reveal><Link to="/services" className="btn-ghost" data-testid="services-all-btn">Full service catalogue <ArrowUpRight className="h-4 w-4" /></Link></Reveal>
      </div>
      <div className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-4 [grid-auto-flow:dense]">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 4) * 0.06} className={`flex ${i === 0 ? "md:col-span-2 md:row-span-2" : ""} ${i === 7 ? "md:col-span-2" : ""}`}>
            <ServiceCard s={s} i={i} big={i === 0} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Industries = () => {
  const others = ALL_INDUSTRIES.filter((i) => !i.slug);
  return (
    <section className="py-24 lg:py-36" data-testid="industries-section">
      <div className="wrap">
        <SectionHead eyebrow="Industries we serve" title="Digital Solutions for Every Industrial Sector" lead="Each sector has its own workflows, compliance pressures and buying journeys. We tailor software, websites and automation to the realities of your industry." />
        <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {INDUSTRY_PAGES.map((ind, i) => (
            <Reveal key={ind.slug} delay={(i % 3) * 0.08}><IndustryCard ind={ind} i={i} /></Reveal>
          ))}
        </div>
        <Reveal className="mt-16 rounded-md border border-stone bg-white p-6 sm:p-10">
          <p className="font-display text-xl font-bold">We also support</p>
          <ul className="mt-6 flex flex-wrap gap-2">
            {others.map((o) => (
              <li key={o.name}>
                <Link to={`/contact?industry=${encodeURIComponent(o.name)}#inquiry`} className="inline-flex min-h-[40px] items-center rounded-full border border-stone px-4 text-[14px] text-graphite transition-colors hover:border-charcoal hover:bg-charcoal hover:text-ivory" data-testid={`industry-chip-${o.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {o.name}
                </Link>
              </li>
            ))}
          </ul>
          <Link to="/industries" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-rust-ink hover:text-charcoal" data-testid="industries-all-link">See all 24 industries <ArrowUpRight className="h-4 w-4" /></Link>
        </Reveal>
      </div>
    </section>
  );
};

export const SolutionsTabs = () => {
  const [active, setActive] = useState(0);
  const g = SOLUTION_GROUPS[active];
  return (
    <div className="mt-16 grid gap-10 lg:grid-cols-[0.9fr_1.1fr]" data-testid="solutions-tabs">
      <div role="tablist" aria-label="Business functions" className="flex flex-col border-t border-charcoal/15">
        {SOLUTION_GROUPS.map((x, i) => (
          <button
            key={x.title}
            role="tab"
            id={`tab-${i}`}
            aria-selected={active === i}
            aria-controls={`panel-${i}`}
            onClick={() => setActive(i)}
            data-testid={`solution-tab-${i}`}
            className={`group flex min-h-[64px] items-center justify-between gap-4 border-b border-charcoal/15 py-5 text-left font-display text-xl font-bold transition-colors sm:text-2xl ${active === i ? "text-charcoal" : "text-graphite/70 hover:text-charcoal"}`}
          >
            <span className="flex items-center gap-5"><span className="font-mono text-xs font-normal text-rust-ink">{String(i + 1).padStart(2, "0")}</span>{x.title}</span>
            <span className={`h-2 w-2 rounded-full transition-colors ${active === i ? "bg-rust" : "bg-transparent"}`} aria-hidden="true" />
          </button>
        ))}
      </div>
      <motion.div
        key={active}
        role="tabpanel"
        id={`panel-${active}`}
        aria-labelledby={`tab-${active}`}
        initial={{ opacity: 0.4, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="relative overflow-hidden rounded-md bg-charcoal p-8 text-ivory sm:p-12"
        data-testid="solution-panel"
      >
        <div className="grid-lines-dark absolute inset-0" aria-hidden="true" />
        <div className="relative">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-rust-deep text-white"><Icon name={g.icon} className="h-6 w-6" /></span>
          <h3 className="mt-8 font-display text-3xl font-bold">{g.title}</h3>
          <ul className="mt-8 grid gap-4 sm:grid-cols-2">
            {g.items.map((it) => (
              <li key={it} className="flex gap-3 border-t border-white/10 pt-4 text-[15px] text-[#E4E0D6]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#E8B795]" />{it}</li>
            ))}
          </ul>
          <Link to={`/services/${g.link}`} className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#E8B795] hover:text-white" data-testid="solution-panel-link">Related service <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
      </motion.div>
    </div>
  );
};

const Solutions = () => (
  <section className="border-t border-stone bg-white py-24 lg:py-36" data-testid="solutions-section">
    <div className="wrap">
      <SectionHead eyebrow="By business function" title="Technology for Every Stage of Your Industrial Business" lead="The final solution is always selected and scoped according to each client's requirements. These groups show where we most often help." />
      <SolutionsTabs />
    </div>
  </section>
);

export const Systems = () => (
  <section className="relative overflow-hidden bg-charcoal py-24 text-ivory lg:py-36 grain" data-testid="systems-section">
    <div className="grid-lines-dark absolute inset-0" aria-hidden="true" />
    <div className="wrap relative">
      <div className="grid gap-10 lg:grid-cols-2 lg:items-end">
        <SectionHead light eyebrow="Manufacturing software" title="Connect Your Business With Smarter Systems" />
        <Reveal><p className="text-base leading-relaxed text-[#D9D5CB] md:text-lg">These are not boxed products. Each system is custom-developed, configured on a suitable platform or integrated with software you already use, depending on what fits your operations best.</p></Reveal>
      </div>
      <ul className="mt-16 grid gap-px overflow-hidden rounded-md border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {SYSTEMS.map(([t, d], i) => (
          <Reveal as="li" key={t} delay={(i % 4) * 0.06} className="group bg-charcoal p-7 transition-colors duration-500 hover:bg-[#30322B]">
            <span className="font-mono text-[11px] text-[#E8B795]">SYS.{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-8 font-display text-lg font-bold leading-snug text-ivory">{t}</h3>
            <p className="mt-3 text-[14px] leading-relaxed text-[#CFCBC0]">{d}</p>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const Why = () => (
  <section className="wrap py-24 lg:py-36" data-testid="why-section">
    <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr]">
      <div>
        <SectionHead eyebrow="Why Industrix Solution" title="A Practical Technology Partner for Industrial Growth" />
        <Reveal className="relative mt-12 aspect-[4/3] overflow-hidden rounded-md [clip-path:polygon(0_0,100%_0,100%_86%,86%_100%,0_100%)]">
          <Img name="worker" alt="Engineer operating industrial machinery on a factory floor" sizes="(min-width: 1024px) 40vw, 100vw" />
        </Reveal>
      </div>
      <ul className="grid content-start gap-x-10 sm:grid-cols-2">
        {WHY.map(([t, d], i) => (
          <Reveal as="li" key={t} delay={(i % 2) * 0.06} className="border-t border-charcoal/15 py-7">
            <span className="font-mono text-xs text-rust-ink">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-3 font-display text-lg font-bold">{t}</h3>
            <p className="mt-2 text-[14px] leading-relaxed text-graphite">{d}</p>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const Process = () => (
  <section className="border-t border-stone bg-[#F1EEE7] py-24 lg:py-36" data-testid="process-section">
    <div className="wrap">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <SectionHead eyebrow="Our working process" title="From Business Challenge to Digital Solution" />
        <Reveal><Link to="/contact#inquiry" className="btn-primary" data-testid="process-cta-btn">Start a Conversation <ArrowRight className="h-4 w-4" /></Link></Reveal>
      </div>
      <div className="mt-16"><Steps steps={PROCESS} /></div>
      <Reveal className="mt-8"><Link to="/process" className="inline-flex items-center gap-2 text-sm font-semibold text-rust-ink hover:text-charcoal" data-testid="process-more-link">How each step works <ArrowUpRight className="h-4 w-4" /></Link></Reveal>
    </div>
  </section>
);

const BusinessTypes = () => (
  <section className="wrap py-24 lg:py-36" data-testid="business-types-section">
    <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
      <SectionHead eyebrow="Who we work with" title="Business Types We Support" lead="From single-plant manufacturers to multi-location enterprises, we scope each engagement to the size, maturity and goals of the business." />
      <ul className="grid sm:grid-cols-2">
        {BUSINESS_TYPES.map((b, i) => (
          <Reveal as="li" key={b} delay={(i % 2) * 0.05} className="group flex items-center gap-5 border-b border-charcoal/15 py-5">
            <span className="font-mono text-xs text-rust-ink">{String(i + 1).padStart(2, "0")}</span>
            <span className="font-display text-[17px] font-semibold transition-transform duration-300 group-hover:translate-x-1">{b}</span>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const Faq = () => (
  <section className="border-t border-stone bg-white py-24 lg:py-36" data-testid="faq-section">
    <div className="wrap grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
      <SectionHead eyebrow="Questions" title="Frequently Asked Questions" lead="Can't find what you are looking for? Send us a message and we will reply with specifics for your project." />
      <Reveal><FaqList faqs={HOME_FAQS} idPrefix="home-faq" /></Reveal>
    </div>
  </section>
);

const FinalCta = () => (
  <section id="inquiry" className="relative overflow-hidden bg-charcoal text-ivory grain" data-testid="final-cta-section">
    <div className="grid-lines-dark absolute inset-0" aria-hidden="true" />
    <div className="wrap relative grid gap-14 py-24 lg:grid-cols-[0.9fr_1.1fr] lg:py-32">
      <Reveal>
        <p className="eyebrow eyebrow-light">Start a conversation</p>
        <h2 className="mt-6 text-4xl font-extrabold leading-[1.02] text-ivory sm:text-5xl lg:text-6xl">Ready to Modernize Your Industrial Business?</h2>
        <p className="mt-8 max-w-xl text-base leading-relaxed text-[#D9D5CB] md:text-lg">Tell us about your business, your current challenges, and the technology you need. Our team can help identify a practical digital solution for your next stage of growth.</p>
        <div className="mt-10 flex flex-wrap gap-3">
          <a href="#inquiry-form" className="btn-primary hover:!bg-ivory hover:!text-charcoal" data-testid="final-discuss-btn">Discuss Your Project <ArrowRight className="h-4 w-4" /></a>
          <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" className="btn-light" data-testid="final-whatsapp-btn"><WhatsAppIcon className="h-4 w-4" /> Contact Us on WhatsApp</a>
        </div>
        <p className="mt-14 font-display text-2xl font-bold text-[#E8B795]" style={{ fontStretch: "120%" }}>{TAGLINE}</p>
      </Reveal>
      <Reveal delay={0.1} className="rounded-md bg-white p-6 text-charcoal sm:p-10" >
        <div id="inquiry-form">
          <p className="font-display text-2xl font-bold">Project inquiry</p>
          <p className="mt-2 text-[14px] text-graphite">Fields marked * are required.</p>
          <div className="mt-8"><InquiryForm compact /></div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Industrix Solution | Manufacturing & Industrial Digital Solutions"
        description="Digital solutions for manufacturers and industrial businesses. Explore custom software, ERP, CRM, industrial websites, mobile apps, automation, and SEO."
        path="/"
        jsonLd={[faqSchema(HOME_FAQS)]}
      />
      <Hero />
      <StatsBar />
      <ClientLogos />
      <Marquee items={["Manufacturing ERP", "Industrial Websites", "B2B Portals", "Smart Factory", "CRM", "Mobile Apps", "Industrial SEO"]} />
      <Challenges />
      <Services />
      <Industries />
      <Solutions />
      <Systems />
      <Why />
      <Testimonials />
      <Process />
      <BusinessTypes />
      <Faq />
      <FinalCta />
    </>
  );
}
